import { View, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText, Caption, Body } from '@/components/ui/Text';
import { Card } from '@/components/ui/Card';

export type Advisory = {
  id: string;
  type:
    | 'weather'
    | 'government'
    | 'safety'
    | 'entry_requirements'
    | 'transport'
    | 'health'
    | 'security'
    | 'natural_disaster'
    | 'news';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  location: string;
  source: string;
  issuedAt: Date;
  expiresAt?: Date;
  isRead?: boolean;
  url?: string;
};

type AdvisoriesListProps = {
  advisories: Advisory[];
  onAdvisoryPress?: (advisory: Advisory) => void;
  emptyMessage?: string;
};

function decodeHtmlEntities(input: string): string {
  if (!input) return input;
  return input
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#(\d+);/g, (_, n) => {
      const code = Number.parseInt(n, 10);
      return Number.isFinite(code) ? String.fromCharCode(code) : '';
    })
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => {
      const code = Number.parseInt(h, 16);
      return Number.isFinite(code) ? String.fromCharCode(code) : '';
    });
}

export function AdvisoriesList({
  advisories,
  onAdvisoryPress,
  emptyMessage = 'No advisories at this time.',
}: AdvisoriesListProps) {
  const theme = useTheme();

  const getSeverityColor = (severity: Advisory['severity']) => {
    switch (severity) {
      case 'critical':
        return theme.colors.error;
      case 'high':
        return '#FF6B35';
      case 'medium':
        return theme.colors.warning;
      case 'low':
        return theme.colors.primary;
      default:
        return theme.colors.textSecondary;
    }
  };

  const getSeverityIcon = (severity: Advisory['severity']) => {
    switch (severity) {
      case 'critical':
        return 'alert-circle';
      case 'high':
        return 'warning';
      case 'medium':
        return 'information-circle';
      case 'low':
        return 'checkmark-circle';
      default:
        return 'information-circle';
    }
  };

  const getTypeIcon = (type: Advisory['type']) => {
    switch (type) {
      case 'weather':
        return 'cloud';
      case 'government':
        return 'flag';
      case 'entry_requirements':
        return 'document-text';
      case 'transport':
        return 'airplane';
      case 'health':
        return 'medkit';
      case 'security':
        return 'warning';
      case 'natural_disaster':
        return 'flame';
      case 'safety':
        return 'shield';
      case 'news':
        return 'newspaper-outline';
      default:
        return 'information-circle';
    }
  };

  if (advisories.length === 0) {
    return (
      <View style={{ paddingVertical: theme.spacing(2) }}>
        <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
          {emptyMessage}
        </Body>
      </View>
    );
  }

  return (
    <View style={{ gap: theme.spacing(1.5) }}>
      {advisories.map((advisory) => {
        const severityColor = getSeverityColor(advisory.severity);
        const severityIcon = getSeverityIcon(advisory.severity);
        const typeIcon = getTypeIcon(advisory.type);
        const title = decodeHtmlEntities(advisory.title);
        const description = decodeHtmlEntities(advisory.description);

        return (
          <Pressable
            key={advisory.id}
            onPress={() => onAdvisoryPress?.(advisory)}
            style={({ pressed }) => [
              {
                opacity: pressed ? 0.85 : 1,
              },
            ]}
          >
            <Card
              variant="outlined"
              style={{
                borderLeftWidth: 4,
                borderLeftColor: severityColor,
                opacity: advisory.isRead ? 0.7 : 1,
              }}
            >
              <View style={{ gap: theme.spacing(1) }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                  <Ionicons name={typeIcon as any} size={20} color={theme.colors.textSecondary} />
                  <ThemedText variant="bodyStrong" style={{ flex: 1 }}>
                    {title}
                  </ThemedText>
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: theme.spacing(0.5),
                      paddingHorizontal: theme.spacing(1),
                      paddingVertical: theme.spacing(0.5),
                      backgroundColor: severityColor + '20',
                      borderRadius: theme.radii.sm,
                    }}
                  >
                    <Ionicons name={severityIcon as any} size={14} color={severityColor} />
                    <Caption color={severityColor} style={{ fontSize: 10, textTransform: 'uppercase' }}>
                      {advisory.severity}
                    </Caption>
                  </View>
                </View>

                <Body color={theme.colors.textSecondary} numberOfLines={2}>
                  {description}
                </Body>

                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
                  <Ionicons name="location-outline" size={14} color={theme.colors.textSecondary} />
                  <Caption color={theme.colors.textSecondary}>{advisory.location}</Caption>
                  <Caption color={theme.colors.textSecondary}>•</Caption>
                  <Caption color={theme.colors.textSecondary}>{advisory.source}</Caption>
                </View>
              </View>
            </Card>
          </Pressable>
        );
      })}
    </View>
  );
}





